import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Order, Driver, SystemConfig, DeliveryProvider, AppMode, OperatingContext, UserProfile } from '../types';
import { DEFAULT_CONFIG, DEFAULT_DRIVERS, DEFAULT_PROVIDERS, LIMITS } from '../constants';
import { supabase } from '../services/supabase';
import { toast } from 'react-hot-toast';
import * as Sentry from "@sentry/react";
import { cleanAndSanitize } from '../utils';
import { usePageVisibility } from './usePageVisibility';
import { applyDriverDailyStats, getLocalDateKey } from '../services/driverDailyProgress';

/**
 * v32.4: SaaS Order Foundation (Supabase Safe)
 * Added: Order archival system support with includeArchived filter
 * Input sanitization via utils/sanitizeInput.ts for XSS prevention.
 */

export const useSalData = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isAuthLoading, setIsAuthLoading] = useState(true);
  const [isDataReady, setIsDataReady] = useState(false);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [storeId, setStoreId] = useState<string | null>(null);
  const [appMode, setAppMode] = useState<AppMode>(AppMode.DISPATCHER);
  const [currentDriverId, setCurrentDriverId] = useState<string | null>(null);
  const [pendingChangesWhileAway, setPendingChangesWhileAway] = useState(false);

  // Detectar visibilidade da página no topo do hook (obrigatório)
  const { isVisible, wasAway } = usePageVisibility({
    debounceMs: 500
  });

  // Refs para funções de refresh que serão chamadas quando a página ficar visível
  const refreshDataRef = useRef<(() => Promise<void>) | null>(null);

  // Effect para salvar estado no sessionStorage para recuperação rápida
  useEffect(() => {
    if (!storeId) return;
    
    // Salvar dados críticos no sessionStorage para recuperação rápida
    const saveToSession = () => {
      try {
        const criticalData = {
          lastSync: Date.now(),
          storeId,
          appMode,
          currentDriverId
        };
        sessionStorage.setItem('sal_critical_state', JSON.stringify(criticalData));
      } catch (e) {
        console.warn('[useSalData] Erro ao salvar estado crítico:', e);
      }
    };

    // Salvar periodicamente e ao mudar estado
    saveToSession();
    const interval = setInterval(saveToSession, 30000); // A cada 30 segundos
    
    return () => clearInterval(interval);
  }, [storeId, appMode, currentDriverId]);

  const [orders, setOrdersState] = useState<Order[]>([]);
  const ordersRef = useRef<Order[]>([]);
  const [archivedOrders, setArchivedOrdersState] = useState<Order[]>([]);
  const archivedOrdersRef = useRef<Order[]>([]);
  const [drivers, setDriversState] = useState<Driver[]>([]);
  const driversRef = useRef<Driver[]>([]);
  const [providers, setProviders] = useState<DeliveryProvider[]>(DEFAULT_PROVIDERS);
  const [rawConfig, setRawConfig] = useState<SystemConfig | null>(null);
  const [context, setContext] = useState<OperatingContext>({
    rainMode: 'off',
    isHighTraffic: false,
    surgeMultiplier: 1.0,
  });

  const config = useMemo(() => {
    if (!rawConfig) return DEFAULT_CONFIG;
    return {
      ...DEFAULT_CONFIG,
      ...rawConfig,
      fleetConfig: { ...DEFAULT_CONFIG.fleetConfig, ...rawConfig.fleetConfig },
      thirdPartyConfig: { ...DEFAULT_CONFIG.thirdPartyConfig, ...rawConfig.thirdPartyConfig },
      cardapioWeb: { ...DEFAULT_CONFIG.cardapioWeb, ...rawConfig.cardapioWeb },
      openDelivery: { ...DEFAULT_CONFIG.openDelivery, ...rawConfig.openDelivery },
      oracle: { ...DEFAULT_CONFIG.oracle, ...rawConfig.oracle },
      communication: { ...DEFAULT_CONFIG.communication, ...rawConfig.communication }
    };
  }, [rawConfig]);

  useEffect(() => {
    ordersRef.current = orders;
  }, [orders]);

  useEffect(() => {
    archivedOrdersRef.current = archivedOrders;
  }, [archivedOrders]);

  useEffect(() => {
    driversRef.current = drivers;
  }, [drivers]);

  useEffect(() => {
    let isMounted = true;

    const resolveIdentity = async (user: { id: string; email?: string | null }) => {
      if (user.email) {
        const { data } = await supabase.rpc('sal_link_identity', { p_email: user.email });
        if (data) return data;
      }
      const { data } = await supabase
        .from('sal_user_identities')
        .select('*')
        .eq('auth_user_id', user.id)
        .maybeSingle();
      return data;
    };

    const clearAuthState = () => {
      setIsAuthenticated(false);
      setStoreId(null);
      setIsDataReady(false);
      setAppMode(AppMode.DISPATCHER);
      setCurrentDriverId(null);
      setUserProfile(null);
      Sentry.setUser(null);
    };

    const applySession = async (session: any) => {
      if (!isMounted) return;

      if (session?.user) {
        const user = session.user;
        setIsDataReady(false);
        Sentry.setUser({ id: user.id, email: user.email || undefined });
        try {
          const identity = await resolveIdentity(user);
          const role = identity?.role as UserProfile['role'] | undefined;
          const identityStoreId = identity?.store_id as string | undefined;

          if (identity && role && identityStoreId) {
            const profile: UserProfile = {
              uid: user.id,
              email: user.email || identity.email,
              role,
              storeId: identityStoreId,
              associatedDriverId: identity.associated_driver_id || undefined
            };
            setUserProfile(profile);
            setStoreId(profile.storeId);
            if (profile.role === 'driver') {
              // Drivers first go to DriverLogin to select their driver account
              setAppMode(AppMode.DRIVER_LOGIN);
              setCurrentDriverId(null); // Will be set when driver selects in DriverLogin
            } else {
              setAppMode(AppMode.DISPATCHER);
              setCurrentDriverId(null);
            }
            setIsAuthenticated(true);
          } else {
            clearAuthState();
            await supabase.auth.signOut();
            toast.error('Conta sem perfil no Supabase. Contate o suporte.');
          }
        } catch (e) {
          console.error('Auth Error', e);
          clearAuthState();
        } finally {
          if (isMounted) setIsAuthLoading(false);
        }
      } else {
        clearAuthState();
        setIsAuthLoading(false);
      }
    };

    const init = async () => {
      const { data } = await supabase.auth.getSession();
      await applySession(data.session);
    };

    init();
    const { data: authListener } = supabase.auth.onAuthStateChange((_event, session) => {
      applySession(session);
    });

    return () => {
      isMounted = false;
      authListener?.subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (!storeId) {
      if (userProfile?.role === 'driver') {
        setRawConfig(DEFAULT_CONFIG);
        setIsDataReady(true);
      }
      return;
    }

    let isMounted = true;

    const currentUserRole = userProfile?.role;

    const orderFromRow = (row: any): Order => {
      const data = row.data || {};
      return {
        ...data,
        id: row.id,
        platformId: row.platform_id ?? data.platformId,
        status: row.status ?? data.status,
        createdAt: row.created_at_ms ?? data.createdAt,
        statusUpdatedAt: row.status_updated_at_ms ?? data.statusUpdatedAt,
        driverId: row.driver_id ?? data.driverId
      } as Order;
    };

    const driverFromRow = (row: any): Driver => ({
      ...row.data,
      id: row.id
    } as Driver);

    const shouldKeepCompletedOrderForStats = (order: Order): boolean => {
      if (order.status === 'delivered') return true;
      return order.status === 'archived' && typeof order.deliveredAt === 'number';
    };

    const loadInitial = async () => {
      try {
        const startOfToday = new Date();
        startOfToday.setHours(0, 0, 0, 0);
        const startOfTodayMs = startOfToday.getTime();

        const todayKey = getLocalDateKey(startOfToday);

        const [configRes, ordersRes, completedTodayRes, driversRes, driverDailyStatsRes, providersRes] = await Promise.all([
          supabase.from('sal_store_config').select('config').eq('store_id', storeId).maybeSingle(),
          supabase
            .from('sal_orders')
            .select('id,data,status,created_at_ms,status_updated_at_ms,driver_id,platform_id')
            .eq('store_id', storeId)
            .not('status', 'in', '("archived","canceled")')
            .order('created_at_ms', { ascending: false })
            .limit(LIMITS.DEFAULT_ORDERS_QUERY_LIMIT),
          supabase
            .from('sal_orders')
            .select('id,data,status,created_at_ms,status_updated_at_ms,driver_id,platform_id')
            .eq('store_id', storeId)
            .in('status', ['archived'])
            .gte('status_updated_at_ms', startOfTodayMs)
            .order('status_updated_at_ms', { ascending: false })
            .limit(LIMITS.DEFAULT_ORDERS_QUERY_LIMIT),
          supabase.from('sal_drivers').select('id,data').eq('store_id', storeId),
          supabase
            .from('sal_driver_daily_stats')
            .select('driver_id,date_key,deliveries_count')
            .eq('store_id', storeId)
            .eq('date_key', todayKey),
          supabase.from('sal_providers').select('providers').eq('store_id', storeId).maybeSingle()
        ]);

        if (!isMounted) return;

        const bootstrapError = configRes.error || ordersRes.error || completedTodayRes.error || driversRes.error || providersRes.error;
        if (bootstrapError) throw bootstrapError;
        if (driverDailyStatsRes.error) {
          console.warn('[useSalData] Contadores diários de motoboy indisponíveis:', driverDailyStatsRes.error.message);
        }

        if (configRes.data?.config) {
          const mergedConfig = { ...DEFAULT_CONFIG, ...configRes.data.config };
          setRawConfig(mergedConfig as SystemConfig);
        } else if (currentUserRole === 'admin') {
          await supabase.from('sal_store_config').upsert({
            store_id: storeId,
            config: DEFAULT_CONFIG,
            updated_at: new Date().toISOString()
          });
          setRawConfig(DEFAULT_CONFIG);
        } else {
          setRawConfig(DEFAULT_CONFIG);
        }

        if (ordersRes.data) {
          const activeOrders = ordersRes.data.map(orderFromRow);
          const completedTodayOrders = (completedTodayRes.data ?? [])
            .map(orderFromRow)
            .filter(shouldKeepCompletedOrderForStats);
          const mergedById = new Map<string, Order>();
          [...activeOrders, ...completedTodayOrders].forEach((order) => mergedById.set(order.id, order));
          setOrdersState(Array.from(mergedById.values()));
        }

        if (driversRes.data) {
          setDriversState(applyDriverDailyStats(driversRes.data.map(driverFromRow), driverDailyStatsRes.data ?? [], startOfToday));
        }

        if (providersRes.data?.providers) {
          setProviders(providersRes.data.providers);
        }
      } catch (error) {
        console.error('Initial bootstrap failed:', error);
        Sentry.captureException(error, { tags: { area: 'bootstrap', action: 'loadInitial' } });
        if (!isMounted) return;
        setRawConfig(DEFAULT_CONFIG);
        setProviders(DEFAULT_PROVIDERS);
      } finally {
        if (isMounted) setIsDataReady(true);
      }
    };

    loadInitial();

    const ordersChannel = supabase
      .channel(`sal-orders-${storeId}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'sal_orders', filter: `store_id=eq.${storeId}` },
        (payload) => {
          // Se a página não está visível, marcar como mudança pendente
          if (typeof document !== 'undefined' && document.visibilityState !== 'visible') {
            setPendingChangesWhileAway(true);
          }
          
          const row = payload.new || payload.old;
          if (!row) return;
          const mapped = orderFromRow(row);
          const isActive = !['archived', 'canceled'].includes(mapped.status);
          const keepForDriverStats = shouldKeepCompletedOrderForStats(mapped);

          setOrdersState((prev) => {
            const existingIdx = prev.findIndex(o => o.id === mapped.id);
            if (payload.eventType === 'DELETE' || (!isActive && !keepForDriverStats)) {
              return prev.filter(o => o.id !== mapped.id);
            }
            if (existingIdx === -1) return [...prev, mapped];
            
            // v60.2: PROTEÇÃO APRIMORADA CONTRA RACE CONDITION EM DISPATCH
            // Verifica timestamps para garantir que não sobrescrevemos dados mais recentes
            const existing = prev[existingIdx];
            const localTimestamp = existing.statusUpdatedAt || existing.createdAt || 0;
            const remoteTimestamp = mapped.statusUpdatedAt || mapped.createdAt || 0;
            const isLocalMoreRecent = localTimestamp > remoteTimestamp;
            
            // Preserva driverId se:
            // 1. Estado local tem driverId mas o Supabase não tem
            // 2. OU estado local é mais recente que o Supabase
            const shouldPreserveDriverId = (existing.driverId && !mapped.driverId) || 
                                          (isLocalMoreRecent && existing.driverId && existing.driverId !== mapped.driverId);
            const shouldPreservePickupConfirmed = existing.pickupConfirmed && !mapped.pickupConfirmed;
            
            // v60.2: Log de diagnóstico aprimorado
            if (shouldPreserveDriverId || shouldPreservePickupConfirmed) {
              console.warn('[useSalData] Proteção anti-race condition ativada:', {
                orderId: mapped.id,
                preservaDriverId: shouldPreserveDriverId,
                preservaPickupConfirmed: shouldPreservePickupConfirmed,
                driverIdLocal: existing.driverId,
                driverIdRemoto: mapped.driverId,
                localTimestamp,
                remoteTimestamp,
                isLocalMoreRecent,
                eventType: payload.eventType,
                timestamp: new Date().toISOString()
              });
              
              // Alerta Sentry para monitoramento
              Sentry.captureMessage('Race condition protection activated', {
                level: 'warning',
                tags: { 
                  feature: 'race_condition_protection',
                  storeId: storeId || 'unknown'
                },
                extra: {
                  orderId: mapped.id,
                  driverIdLocal: existing.driverId,
                  driverIdRemoto: mapped.driverId,
                  isLocalMoreRecent,
                  localTimestamp,
                  remoteTimestamp
                }
              });
            }
            
            const next = [...prev];
            next[existingIdx] = {
              ...mapped,
              // Preserva campos críticos se o estado local tem mas o Supabase não tem ainda
              // (pode acontecer em race conditions durante dispatch)
              ...(shouldPreserveDriverId && { driverId: existing.driverId }),
              ...(shouldPreservePickupConfirmed && { 
                pickupConfirmed: existing.pickupConfirmed,
                pickupConfirmedAt: existing.pickupConfirmedAt 
              }),
              // Preserva campos de estratégia manual se o Supabase os limpou
              manualAssignment: mapped.manualAssignment ?? existing.manualAssignment,
              manualRouteGroup: mapped.manualRouteGroup ?? existing.manualRouteGroup,
              smartClusterId: mapped.smartClusterId ?? existing.smartClusterId,
            };
            return next;
          });
        }
      )
      .subscribe();

    const driversChannel = supabase
      .channel(`sal-drivers-${storeId}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'sal_drivers', filter: `store_id=eq.${storeId}` },
        (payload) => {
          // Se a página não está visível, marcar como mudança pendente
          if (typeof document !== 'undefined' && document.visibilityState !== 'visible') {
            setPendingChangesWhileAway(true);
          }
          
          const row = payload.new || payload.old;
          if (!row) return;
          const mapped = driverFromRow(row);

          setDriversState((prev) => {
            const existingIdx = prev.findIndex(d => d.id === mapped.id);
            if (payload.eventType === 'DELETE') return prev.filter(d => d.id !== mapped.id);
            if (existingIdx === -1) return [...prev, mapped];
            const next = [...prev];
            const existing = prev[existingIdx];
            const todayKey = getLocalDateKey();
            next[existingIdx] = {
              ...mapped,
              dailyDeliveriesDate: existing.dailyDeliveriesDate === todayKey ? existing.dailyDeliveriesDate : mapped.dailyDeliveriesDate,
              dailyDeliveriesCount: existing.dailyDeliveriesDate === todayKey
                ? Math.max(existing.dailyDeliveriesCount || 0, mapped.dailyDeliveriesCount || 0)
                : mapped.dailyDeliveriesCount,
            };
            return next;
          });
        }
      )
      .subscribe();

    const driverDailyStatsChannel = supabase
      .channel(`sal-driver-daily-stats-${storeId}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'sal_driver_daily_stats', filter: `store_id=eq.${storeId}` },
        (payload) => {
          const row = payload.new as { driver_id?: string; date_key?: string; deliveries_count?: number } | null;
          if (!row?.driver_id || row.date_key !== getLocalDateKey()) return;

          setDriversState((prev) => prev.map((driver) => {
            if (driver.id !== row.driver_id) return driver;
            return {
              ...driver,
              dailyDeliveriesDate: row.date_key,
              dailyDeliveriesCount: Math.max(driver.dailyDeliveriesCount || 0, row.deliveries_count || 0),
            };
          }));
        }
      )
      .subscribe();

    const configChannel = supabase
      .channel(`sal-config-${storeId}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'sal_store_config', filter: `store_id=eq.${storeId}` },
        (payload) => {
          const nextConfig = (payload.new as { config?: SystemConfig } | null)?.config;
          if (nextConfig) {
            // Merge com defaults para garantir que novos campos tenham valores padrão
            const mergedConfig = { ...DEFAULT_CONFIG, ...nextConfig };
            setRawConfig(mergedConfig as SystemConfig);
            setIsDataReady(true);
          }
        }
      )
      .subscribe();

    const providersChannel = supabase
      .channel(`sal-providers-${storeId}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'sal_providers', filter: `store_id=eq.${storeId}` },
        (payload) => {
          const nextProviders = (payload.new as { providers?: DeliveryProvider[] } | null)?.providers;
          if (nextProviders) setProviders(nextProviders);
        }
      )
      .subscribe();

    // Registrar função de refresh para uso externo
    const doRefresh = async () => {
      if (!storeId || !isMounted) return;
      
      console.log('[useSalData] Refresh de dados após volta da aba...');
      
      try {
        const startOfToday = new Date();
        startOfToday.setHours(0, 0, 0, 0);
        const startOfTodayMs = startOfToday.getTime();

        // Recarregar orders ativas + concluídas arquivadas de hoje para manter estatísticas do motoboy
        const [freshOrdersRes, freshCompletedTodayRes] = await Promise.all([
          supabase
            .from('sal_orders')
            .select('id,data,status,created_at_ms,status_updated_at_ms,driver_id,platform_id')
            .eq('store_id', storeId)
            .not('status', 'in', '("archived","canceled")')
            .order('created_at_ms', { ascending: false })
            .limit(LIMITS.DEFAULT_ORDERS_QUERY_LIMIT),
          supabase
            .from('sal_orders')
            .select('id,data,status,created_at_ms,status_updated_at_ms,driver_id,platform_id')
            .eq('store_id', storeId)
            .in('status', ['archived'])
            .gte('status_updated_at_ms', startOfTodayMs)
            .order('status_updated_at_ms', { ascending: false })
            .limit(LIMITS.DEFAULT_ORDERS_QUERY_LIMIT),
        ]);

        const freshOrders = freshOrdersRes.data;
        const freshCompletedToday = freshCompletedTodayRes.data;
        
        if (freshOrders && isMounted) {
          // v60.3: MERGE inteligente em vez de replace completo
          // Protege contra sobrescrita de dados locais recentes
          const now = Date.now();
          const LOCAL_CHANGE_COOLDOWN_MS = 5000; // 5 segundos após mudança local
          
          setOrdersState((prevOrders) => {
            const freshCompletedForStats = (freshCompletedToday ?? [])
              .map(orderFromRow)
              .filter(shouldKeepCompletedOrderForStats);
            const freshById = new Map([...freshOrders, ...freshCompletedForStats].map((rowOrOrder: any) => {
              const order = rowOrOrder.data ? orderFromRow(rowOrOrder) : rowOrOrder;
              return [order.id, order] as [string, Order];
            }));
            
            const refreshed = prevOrders.map(existingOrder => {
              const freshOrder = freshById.get(existingOrder.id);
              if (!freshOrder) return existingOrder;
              
              // Se temos uma mudança local recente, preservar
              const timeSinceLocalUpdate = now - (existingOrder.statusUpdatedAt || 0);
              const isLocalChangeRecent = timeSinceLocalUpdate < LOCAL_CHANGE_COOLDOWN_MS;
              
              // Preservar driverId se mudança local é recente e remoto não tem
              if (isLocalChangeRecent && existingOrder.driverId && !freshOrder.driverId) {
                console.warn('[useSalData] Preservando driverId em refresh:', {
                  orderId: existingOrder.id,
                  driverId: existingOrder.driverId,
                  timeSinceUpdate: timeSinceLocalUpdate
                });
                return {
                  ...freshOrder,
                  driverId: existingOrder.driverId,
                  pickupConfirmed: existingOrder.pickupConfirmed,
                  pickupConfirmedAt: existingOrder.pickupConfirmedAt
                };
              }
              
              return freshOrder;
            });

            const existingIds = new Set(refreshed.map(order => order.id));
            const missingFreshOrders = Array.from(freshById.values()).filter(order => !existingIds.has(order.id));
            return [...refreshed, ...missingFreshOrders];
          });
        }
        
        const todayKey = getLocalDateKey(startOfToday);

        // Recarregar drivers + contadores diários persistidos
        const [freshDriversRes, freshDriverDailyStatsRes] = await Promise.all([
          supabase
            .from('sal_drivers')
            .select('id,data')
            .eq('store_id', storeId),
          supabase
            .from('sal_driver_daily_stats')
            .select('driver_id,date_key,deliveries_count')
            .eq('store_id', storeId)
            .eq('date_key', todayKey),
        ]);

        const freshDrivers = freshDriversRes.data;
        const freshDriverDailyStats = freshDriverDailyStatsRes.data;
        
        if (freshDrivers && isMounted) {
          setDriversState(applyDriverDailyStats(freshDrivers.map(driverFromRow), freshDriverDailyStats ?? [], startOfToday));
        }
        
        // Resetar estado de mudanças pendentes após refresh bem-sucedido
        if (isMounted) {
          setPendingChangesWhileAway(false);
        }
        
        console.log('[useSalData] Dados atualizados com sucesso');
      } catch (error) {
        console.error('[useSalData] Erro ao refresh dados:', error);
      }
    };

    // Armazenar a função de refresh na ref
    refreshDataRef.current = doRefresh;

    return () => {
      isMounted = false;
      supabase.removeChannel(ordersChannel);
      supabase.removeChannel(driversChannel);
      supabase.removeChannel(driverDailyStatsChannel);
      supabase.removeChannel(configChannel);
      supabase.removeChannel(providersChannel);
    };
  }, [storeId, userProfile]);

  // Effect separado para detectar visibilidade da página e chamar refresh
  useEffect(() => {
    // Quando a página volta a ficar visível após ter estado oculta
    if (wasAway && isVisible && refreshDataRef.current) {
      console.log('[useSalData] Página ficou visível, executando refresh...');
      
      // Notificar usuário sobre atualização
      if (pendingChangesWhileAway) {
        toast.success('Dados atualizados!', { 
          id: 'page-visible-notification',
          duration: 2000 
        });
      }
      
      // Pequeno delay para garantir que a conexão esteja pronta
      const timer = setTimeout(() => {
        refreshDataRef.current?.();
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [wasAway, isVisible, pendingChangesWhileAway]);

  const orderToRow = (o: Order) => ({
    store_id: storeId,
    id: o.id,
    data: cleanAndSanitize(o),
    status: o.status,
    created_at_ms: o.createdAt,
    status_updated_at_ms: o.statusUpdatedAt || o.createdAt,
    driver_id: o.driverId || null,
    platform_id: o.platformId || null,
    updated_at: new Date().toISOString()
  });

  const driverToRow = (d: Driver) => {
    const { dailyDeliveriesDate, dailyDeliveriesCount, totalDeliveries, ...persistedDriver } = d;
    return {
      store_id: storeId,
      id: d.id,
      data: cleanAndSanitize(persistedDriver),
      updated_at: new Date().toISOString()
    };
  };

  const cloudActions = {
    setOrders: async (updater: any) => {
      if (!storeId) return;
      const previousOrders = ordersRef.current;
      const baselineById = new Map(
        previousOrders.map((order) => [order.id, JSON.stringify(cleanAndSanitize(order))])
      );

      const candidateNext = typeof updater === 'function' ? updater(previousOrders) : updater;
      const nextOrders = Array.isArray(candidateNext) ? candidateNext : previousOrders;

      ordersRef.current = nextOrders;
      setOrdersState(nextOrders);

      try {
        const changed = nextOrders.filter((nO: Order) => {
          const prevSerialized = baselineById.get(nO.id);
          const nextSerialized = JSON.stringify(cleanAndSanitize(nO));
          if (!prevSerialized) return true;
          return prevSerialized !== nextSerialized;
        });

        if (changed.length > 0) {
          const { error } = await supabase
            .from('sal_orders')
            .upsert(changed.map(orderToRow), { onConflict: 'store_id,id' });

          if (error) throw error;
        }
      } catch (e) {
        console.error("Supabase Save Error:", e);
        ordersRef.current = previousOrders;
        setOrdersState(previousOrders);
        toast.error('Falha ao salvar alteracoes de pedidos.');
      }
    },
    deleteOrder: async (id: string) => {
      if (!storeId) return;
      const target = orders.find(o => o.id === id);
      if (!target) return;
      const updated = { ...target, status: 'archived' as Order['status'], statusUpdatedAt: Date.now() };
      await supabase.from('sal_orders').upsert(orderToRow(updated), { onConflict: 'store_id,id' });
    },

    deleteMultipleOrders: async (ids: string[]) => {
      if (!storeId || ids.length === 0) return;
      try {
        const updates = orders
          .filter(o => ids.includes(o.id))
          .map(o => ({ ...o, status: 'archived' as Order['status'], statusUpdatedAt: Date.now() }));
        if (updates.length === 0) return;
        await supabase
          .from('sal_orders')
          .upsert(updates.map(orderToRow), { onConflict: 'store_id,id' });
      } catch (e) { console.error("Batch Delete Error:", e); }
    },

    /**
     * Load archived orders from archive table
     * @param options.limit Max number of archived orders to load (default 100)
     * @param options.offset Offset for pagination
     */
    loadArchivedOrders: async (options?: { limit?: number; offset?: number }) => {
      if (!storeId) return;
      const limit = options?.limit ?? 100;
      const offset = options?.offset ?? 0;
      
      try {
        const { data, error } = await supabase
          .from('sal_order_archives')
          .select('id,data,status,created_at_ms,status_updated_at_ms,driver_id,platform_id,archived_at,archive_reason')
          .eq('store_id', storeId)
          .order('archived_at', { ascending: false })
          .range(offset, offset + limit - 1);

        if (error) throw error;

        const archived = (data ?? []).map((row: any) => ({
          ...row.data,
          id: row.id,
          platformId: row.platform_id ?? row.data?.platformId,
          status: row.status ?? row.data?.status,
          createdAt: row.created_at_ms ?? row.data?.createdAt,
          statusUpdatedAt: row.status_updated_at_ms ?? row.data?.statusUpdatedAt,
          driverId: row.driver_id ?? row.data?.driverId,
          _archivedAt: row.archived_at,
          _archiveReason: row.archive_reason,
        } as Order & { _archivedAt?: string; _archiveReason?: string }));

        setArchivedOrdersState(archived);
      } catch (e) {
        console.error("Load Archived Orders Error:", e);
        toast.error('Falha ao carregar pedidos arquivados.');
      }
    },

    /**
     * Restore an archived order back to active orders
     */
    restoreOrder: async (orderId: string) => {
      if (!storeId) return;
      
      try {
        const { data, error } = await supabase.rpc('sal_restore_order', {
          p_store_id: storeId,
          p_order_id: orderId,
        });

        if (error) throw error;

        if (data === true) {
          setArchivedOrdersState(prev => prev.filter(o => o.id !== orderId));
          toast.success('Pedido restaurado com sucesso!');
          return true;
        } else {
          toast.error('Falha ao restaurar pedido.');
          return false;
        }
      } catch (e) {
        console.error("Restore Order Error:", e);
        toast.error('Falha ao restaurar pedido.');
        return false;
      }
    },

    /**
     * Permanently delete an archived order
     */
    deleteArchivedOrder: async (orderId: string) => {
      if (!storeId) return;
      
      try {
        const { error } = await supabase
          .from('sal_order_archives')
          .delete()
          .eq('store_id', storeId)
          .eq('id', orderId);

        if (error) throw error;

        setArchivedOrdersState(prev => prev.filter(o => o.id !== orderId));
        toast.success('Pedido excluido permanentemente.');
        return true;
      } catch (e) {
        console.error("Delete Archived Order Error:", e);
        toast.error('Falha ao excluir pedido.');
        return false;
      }
    },

    /**
     * Run archive process manually
     * @param retentionDays Orders older than this will be archived (default 90)
     */
    runArchive: async (retentionDays?: number) => {
      try {
        const { data, error } = await supabase.rpc('sal_auto_archive_orders', {
          p_retention_days: retentionDays ?? 90,
          p_batch_size: 1000,
        });

        if (error) throw error;

        const result = Array.isArray(data) ? data[0] : data;
        toast.success(`Arquivados: ${result?.archived_count ?? 0} | Removidos: ${result?.deleted_count ?? 0}`);
        return result;
      } catch (e) {
        console.error("Archive Error:", e);
        toast.error('Falha ao executar arquivo.');
        return null;
      }
    },

    setDrivers: async (updater: any) => {
      if (!storeId) return;
      const previousDrivers = driversRef.current;
      const baselineById = new Map(
        previousDrivers.map((driver) => [driver.id, JSON.stringify(cleanAndSanitize(driver))])
      );
      const candidateNext = typeof updater === 'function' ? updater(previousDrivers) : updater;
      const nextState = Array.isArray(candidateNext) ? candidateNext : previousDrivers;

      driversRef.current = nextState;
      setDriversState(nextState);

      try {
        const changed = nextState.filter((driver: Driver) => {
          const prevSerialized = baselineById.get(driver.id);
          const nextSerialized = JSON.stringify(cleanAndSanitize(driver));
          if (!prevSerialized) return true;
          return prevSerialized !== nextSerialized;
        });

        if (changed.length > 0) {
          const { error } = await supabase
            .from('sal_drivers')
            .upsert(changed.map(driverToRow), { onConflict: 'store_id,id' });

          if (error) throw error;
        }
      } catch (e) {
        console.error("Supabase Drivers Save Error:", e);
        driversRef.current = previousDrivers;
        setDriversState(previousDrivers);
        toast.error('Falha ao salvar alteracoes de motoboys.');
      }
    },
    deleteDriver: async (id: string) => {
      if (!storeId) return;
      const target = drivers.find(d => d.id === id);
      if (!target) return;
      const updated = { ...target, status: 'offline' as Driver['status'] };
      await supabase
        .from('sal_drivers')
        .upsert([driverToRow(updated)], { onConflict: 'store_id,id' });
    },
    setConfig: async (cfg: SystemConfig) => {
      if (!storeId) return;
      const previousConfig = rawConfig;
      const nextConfig = cleanAndSanitize(cfg) as SystemConfig;

      // Atualização otimista para evitar UI "travada" enquanto aguarda roundtrip do banco
      setRawConfig(nextConfig);

      const { error } = await supabase
        .from('sal_store_config')
        .upsert(
          { store_id: storeId, config: nextConfig, updated_at: new Date().toISOString() },
          { onConflict: 'store_id' }
        );

      if (error) {
        console.error('Supabase Config Save Error:', error);
        Sentry.captureException(error, { tags: { area: 'settings', action: 'setConfig' } });
        setRawConfig(previousConfig ?? DEFAULT_CONFIG);
        toast.error('Nao foi possivel salvar as configuracoes.');
      }
    },
    setProviders: async (prv: DeliveryProvider[]) => storeId && supabase
      .from('sal_providers')
      .upsert({ store_id: storeId, providers: prv.map(p => cleanAndSanitize(p)), updated_at: new Date().toISOString() })
  };

  return { ...cloudActions, isAuthenticated, isAuthLoading, isDataReady, userProfile, appMode, setAppMode, currentDriverId, setCurrentDriverId, orders, archivedOrders, drivers, providers, config, context, setContext, storeId, pendingChangesWhileAway };
};
